import React, { useCallback, useMemo, useEffect, useState } from 'react';

import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import addLabel from 'helpers/addLabel';
import addRecent from 'helpers/addRecent';
import sortByRecent from './utils/sortByRecent';
import { TopicFilterNames, ITopicFilterState } from './shared';
import { IFilterInfo } from 'redux/reducers/usage/filter/filter';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'redux/rootReducer';
import { ICountryFlagProps } from 'types/shared';
import { loadCountries } from 'redux/reducers/countries';
import { loadExCountries } from 'redux/reducers/exCountries';
import ExtendedCountryDropdown from 'components/base/TopicFiltersDropdown/CountryDropdown/ExtendedCountryDropdown';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import styled from 'styled-components/macro';

import { ReactComponent as IconInclude } from 'icons/icon-include.svg';
import { ReactComponent as IconExclude } from 'icons/icon-exclude.svg';
import { ReactComponent as IconClearFilter } from 'icons/icon-clear-filter.svg';
import Box from 'components/elements/Box';
import sortByDisplayName from 'helpers/sortByDisplayName';
import { Button, ButtonVariant } from './TopicFilters.styles';

interface ICountryFilterContainerProps {
  setFilter: any;
  clearFilterUrl: any;
  recentFilters: IFilterInfo[];
  filters: ITopicFilterState;
  disabled?: boolean;
}

// For some reason Firefox hides text shorter than 47px
const FilterWrapper = styled('div')<any>`
  button > div > span:first-of-type {
    min-width: 47px;
  }
`;

type activeScreen = 'include' | 'exclude';

const CountryFilterContainer: React.FC<ICountryFilterContainerProps> = (
  props
) => {
  const { setFilter, clearFilterUrl, recentFilters, filters, disabled } = props;

  const countriesFromFilters =
    typeof filters?.country === 'string'
      ? [filters?.country]
      : filters?.country;

  const [isActiveMainSection, setIsActiveMainSection] = useState<boolean>(true);
  const [activeScreen, setActiveScreen] = useState<activeScreen>('include');

  const dispatch = useDispatch();

  const getRecentFiltersFor = useCallback(
    (filterName: TopicFilterNames): IFilterInfo[] => {
      const result = recentFilters.filter(
        (recent: any) => recent.filterType === filterName
      );
      result.splice(3);
      return result;
    },
    [recentFilters]
  );

  const incCountries = useSelector((state: RootState) => state.countries.data)
    .map(addLabel)
    .sort(sortByDisplayName);
  const exCountries = useSelector((state: RootState) => state.exCountries.data)
    .map((country) => addLabel(country, undefined, undefined, 'EX '))
    .sort(sortByDisplayName);

  const sortedCountries = useMemo(() => {
    let countriesToUse = [];
    if (countriesFromFilters === undefined) {
      countriesToUse = [...incCountries, ...exCountries];
    } else {
      countriesToUse = [
        ...(countriesFromFilters[0]?.includes('EX')
          ? exCountries
          : incCountries),
      ];
    }

    const sorted = countriesToUse
      .map(addRecent(getRecentFiltersFor('country')))
      .sort(sortByRecent);
    return [...sorted];
  }, [incCountries, exCountries, getRecentFiltersFor, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(loadCountries());
    dispatch(loadExCountries());
  }, [dispatch]);

  const selectedFilter = sortedCountries.filter(
    (country: any): boolean => !!countriesFromFilters?.includes(country.id)
  );

  const clearFilter = (): void => {
    if (countriesFromFilters) {
      clearFilterUrl('country');
      logToAmplitude('Filter has been updated', { type: 'Territory' });
    }
  };

  const listOfCountries = useMemo(() => {
    const countries = activeScreen === 'include' ? incCountries : exCountries;
    return countries.map((c: ICountryFlagProps) => ({
      ...c,
      isRecent: false,
    }));
  }, [activeScreen, incCountries, exCountries]);

  const onChange = (item: any): void => {
    const isChecked = countriesFromFilters?.includes(item?.id);

    let newCountriesList = [item?.id];
    if (isChecked && countriesFromFilters?.length) {
      // remove from current countries
      newCountriesList = countriesFromFilters?.filter(
        (country: any) => country !== item?.id
      );
    } else if (countriesFromFilters?.length) {
      // add to current countries
      newCountriesList = [...countriesFromFilters, item?.id];
    }

    if (newCountriesList.length) {
      setFilter('country', newCountriesList);
      logToAmplitude('Filter has been updated', {
        type: 'Territory',
        value: newCountriesList,
      });
    } else {
      clearFilter();
    }
  };

  const selectedItemsIds = selectedFilter.map((item: any): any => item.id);

  const itemsWithoutSelected = sortedCountries.filter(
    (item: any) => !selectedItemsIds.includes(item.id)
  );

  return (
    <FilterWrapper>
      {isActiveMainSection && (
        <ExtendedCountryDropdown
          onClose={(): void => setIsActiveMainSection(true)}
          onChange={onChange}
          items={itemsWithoutSelected}
          defaultLabel="Global"
          selectedItems={!disabled ? selectedFilter : []}
          selectedItem={!disabled ? selectedFilter[0] || null : null}
          itemToString={(item: any): string => item?.id}
          selectedItemsIds={!disabled ? selectedItemsIds : []}
          isDisplayedWithFlag={false}
          showAllItems={false}
          isMultipleSelection
          showOnlyRecent
          clearFilter={clearFilter}
          disabled={disabled}
        >
          <Box
            paddingRight={3}
            paddingLeft={3}
            marginTop={2}
            marginBottom={2}
            borderTop={'1px solid #E8EBED'}
          >
            <ButtonVariant
              onClick={(): void => {
                setActiveScreen('include');
                setIsActiveMainSection(false);
              }}
              disabled={
                !!(
                  countriesFromFilters?.length &&
                  countriesFromFilters[0]?.includes('EX')
                )
              }
            >
              <TippyToolTip
                tooltipContent="You could only select coutries or EX countries"
                enabled={
                  !!(
                    countriesFromFilters?.length &&
                    countriesFromFilters[0]?.includes('EX')
                  )
                }
                zIndex="9999"
                placement="top"
              >
                <IconInclude color="#001247" />
                <span>SELECT COUNTRY</span>
              </TippyToolTip>
            </ButtonVariant>

            <ButtonVariant
              onClick={(): void => {
                setActiveScreen('exclude');
                setIsActiveMainSection(false);
              }}
              disabled={
                !!(
                  countriesFromFilters?.length &&
                  !countriesFromFilters[0]?.includes('EX')
                )
              }
            >
              <TippyToolTip
                tooltipContent="You could only select coutries or EX countries"
                enabled={
                  !!(
                    countriesFromFilters?.length &&
                    !countriesFromFilters[0]?.includes('EX')
                  )
                }
                zIndex="9999"
                placement="top"
              >
                <IconExclude />
                <span>EXCLUDE COUNTRY</span>
              </TippyToolTip>
            </ButtonVariant>
            <Button
              borderColor={'LightGrey'}
              borderWidth={'1px'}
              borderStyle={'solid'}
              fontSize={14}
              py={'3.8px'}
              isActive={!!(selectedFilter[0] || null)}
              onClick={clearFilter}
              isClearButton
            >
              <IconClearFilter />
              Clear this filter
            </Button>
          </Box>
        </ExtendedCountryDropdown>
      )}
      {!isActiveMainSection && (
        <ExtendedCountryDropdown
          onClose={(): void => setIsActiveMainSection(true)}
          onChange={onChange}
          items={listOfCountries}
          selectedItem={selectedFilter[0] || null}
          defaultLabel="Global"
          isOpened={true}
          isExclude={activeScreen === 'exclude'}
          itemToString={(item: any): string => item?.id}
          selectedItemsIds={selectedItemsIds}
          isDisplayedWithFlag={false}
          isMultipleSelection
          clearFilter={clearFilter}
        >
          <Button
            borderColor={'LightGrey'}
            borderWidth={'1px'}
            borderStyle={'solid'}
            fontSize={13}
            py={4}
            isActive={!!(selectedFilter[0] || null)}
            onClick={clearFilter}
            isClearButton
          >
            <IconClearFilter />
            Clear this filter
          </Button>
        </ExtendedCountryDropdown>
      )}
    </FilterWrapper>
  );
};

export default CountryFilterContainer;
